/* @jsx h */
import { defineAsyncComponent, defineComponent, h } from "vue";
import { cn } from "../utils";

export interface RichTextEditorProps {
  /** Current value as an HTML string. */
  value?: string | null;
  /** Called with the new HTML whenever the content changes ("" when empty). */
  onChange: (html: string) => void;
  /** Optional submit handler. When present, Enter submits and Ctrl/Cmd+Enter inserts a line break. */
  onSubmit?: () => void;
  className?: string;
  disabled?: boolean;
}

const RichTextEditorLoading = defineComponent<RichTextEditorProps>({
  name: "RichTextEditorLoading",
  props: ["value", "onChange", "onSubmit", "className", "disabled"],
  setup(props) {
    return () => (
      <textarea
        class={cn(
          "textarea textarea-bordered min-h-[157px] w-full resize-none",
          props.className,
        )}
        disabled
        value={props.value || ""}
      />
    );
  },
});

const InnerRichTextEditor = defineAsyncComponent({
  loader: () => import("./InnerRichTextEditor"),
  loadingComponent: RichTextEditorLoading,
  delay: 0,
});

/**
 * Shared rich-text editor. The Tiptap implementation is lazy-loaded so the
 * outer module stays small until the editor is actually rendered.
 *
 * Emits HTML strings. Those MUST be sanitized server-side at write time before
 * being stored / rendered / emailed.
 */
export const RichTextEditor = defineComponent<RichTextEditorProps>({
  name: "RichTextEditor",
  props: ["value", "onChange", "onSubmit", "className", "disabled"],
  setup(props) {
    return () => (
      <InnerRichTextEditor
        value={props.value}
        onChange={props.onChange}
        onSubmit={props.onSubmit}
        className={props.className}
        disabled={props.disabled}
      />
    );
  },
});
